import { Button, Card, SvgIcon, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { RiAddLine } from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { MainSectionTemplate } from '../../../components/AdminTemplates';
import CustomDataGrid from '../../../components/CustomDataGrid';
import VesselOrCrewStatusBadge, {
  ExpiringSoonBadge,
} from '../../../components/VesselOrCrewStatusBadge';
import { useAppDataContext } from '../../../contexts/appData';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { CrewMemberListItem } from '../../../types/crew';
import AdminTableDetailLink from '../../../components/AdminTableDetailLink';
import { CrewMemberRole, CrewMemberStatus } from '../../../types/core';
import { useDataGridSearchParamsFiltersManager } from '../../../hooks/useSearchParamsFilters';

export const CrewPage = () => {
  const { t } = useLanguage();
  const { isOperator } = useCurrentUser();
  const { getCrewMembers } = useIotsApi();
  const { appData } = useAppDataContext();

  const {
    data: crewMembers = [],
    isFetching,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['crew-members'],
    queryFn: () => getCrewMembers(),
    // Default sorting of crew members for the list based on the first 3 columns
    select: crewMembers =>
      crewMembers
        .filter(
          /* Only non deleted crew members will be displayed */
          ({ deleteDate }) => !deleteDate,
        )
        .sort(
          (a, b) =>
            a.operatorName.localeCompare(b.operatorName) ||
            a.firstName.localeCompare(b.firstName) ||
            a.firstName.localeCompare(b.lastName),
        ),
  });

  useQueryErrorNotifier([error]);

  const { initialFilterModel, onFilterModelChange } =
    useDataGridSearchParamsFiltersManager();

  const columns = useMemo<GridColDef<CrewMemberListItem>[]>(
    () => [
      {
        field: 'firstName',
        flex: 3,
        headerName: t('content.first-name'),
        minWidth: 200,
      },
      {
        field: 'lastName',
        flex: 3,
        headerName: t('content.last-name'),
        minWidth: 160,
      },
      ...(!isOperator
        ? [
            {
              field: 'operatorName',
              flex: 3,
              headerName: t('content.operator'),
              minWidth: 220,
            },
          ]
        : []),
      {
        type: 'singleSelect',
        field: 'role',
        headerName: t('content.role'),
        minWidth: 160,
        flex: 2,
        valueOptions: appData.crewMemberRole.map(crewMemberRole => ({
          value: crewMemberRole,
          label: t(`crew-member-roles.${crewMemberRole}`),
        })),
      },
      {
        type: 'number',
        field: 'pendingCertificationsCount',
        headerName: t('content.pending-certifications'),
        minWidth: 180,
        flex: 2,
        cellClassName: params => (params.value > 0 ? 'bg-iots-red-500/10' : ''),
      },
      {
        type: 'number',
        field: 'validCertificationsCount',
        headerName: t('content.valid-certifications'),
        flex: 2,
        minWidth: 180,
        cellClassName: params =>
          params.row.role === CrewMemberRole.Skipper && !params.value
            ? 'bg-iots-red-500/10'
            : '',
        renderCell: params => (
          <Tooltip
            key={`crew-certifications-tooltip-${params.id}`}
            title={t('validation.valid-certifications-minimum')}
            disableHoverListener={Boolean(params.value)}
          >
            <div>{params.value}</div>
          </Tooltip>
        ),
      },
      {
        type: 'singleSelect',
        field: 'status',
        headerName: t('content.status'),
        display: 'flex',
        minWidth: 184,
        valueOptions: appData.crewMemberStatus.map(status => ({
          value: status,
          label: t(`crew-member-status.${status}`),
        })),
        renderCell: params => (
          <VesselOrCrewStatusBadge
            entity="crew-member"
            status={params.value as CrewMemberStatus}
            small
          />
        ),
      },
      {
        type: 'singleSelect',
        headerName: t('content.expiring-soon'),
        renderHeader: () => (
          <ExpiringSoonBadge inheritColor entity="crew-member" />
        ),
        field: 'expiredSoon',
        display: 'flex',
        valueOptions: [
          {
            value: true,
            label: t(`content.expiring-soon`),
          },
          {
            value: false,
            label: t(`content.not-expiring-soon`),
          },
        ],
        renderCell: params =>
          params.row.expiredSoon && <ExpiringSoonBadge entity="crew-member" />,
      },
      {
        field: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        display: 'flex',
        renderCell: params => (
          <AdminTableDetailLink
            isLoading={isFetching}
            href={`/admin/crew/${params.id}`}
          />
        ),
      },
    ],
    [isOperator, isFetching, t, appData],
  );

  return (
    <MainSectionTemplate
      section={t('content.fleet')}
      title={t('content.crew')}
      isLoading={isFetching}
      actions={
        <div className="flex flex-col items-center gap-2">
          <Link to="/admin/crew/create">
            <Button
              variant="contained"
              startIcon={<SvgIcon component={RiAddLine} />}
              size="small"
            >
              {t('content.add-new')}
            </Button>
          </Link>
        </div>
      }
    >
      <Card variant="outlined">
        {!crewMembers.length && !isFetching ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <h1 className="text-4xl font-bold">
                {t('content.no-crew-found')}
              </h1>
            </div>
          </div>
        ) : (
          <CustomDataGrid
            rows={crewMembers}
            loading={isLoading}
            columns={columns}
            onFilterModelChange={onFilterModelChange}
            initialState={{
              filter: {
                filterModel: initialFilterModel,
              },
              sorting: {
                sortModel: [
                  {
                    field: 'firstName',
                    sort: 'asc',
                  },
                ],
              },
            }}
          />
        )}
      </Card>
    </MainSectionTemplate>
  );
};

export default CrewPage;
